<template>
  <span ref="root" class="field-help">
    <button
      :aria-expanded="String(open)"
      :aria-label="$t('buttons.field_help')"
      class="field-help-button"
      type="button"
      @click="open = !open">
      <feather-icon icon="InfoIcon" svgClasses="w-4 h-4" />
    </button>
    <span v-if="open" :style="position" class="field-help-popover" role="tooltip">{{ text }}</span>
  </span>
</template>

<script>
// An info icon that shows a field's help text while pressed open; a click elsewhere, Escape or scrolling closes it.
// The text is fixed to the viewport above everything else, so a later field (an editor toolbar, a select) cannot show
// through it.
export default {
  name: "FieldHelp",

  props: {
    text: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      open: false,
      position: {},
    }
  },

  watch: {
    open(isOpen) {
      if (isOpen) {
        const icon = this.$refs.root.getBoundingClientRect()
        const left = Math.max(8, Math.min(icon.left - 8, window.innerWidth - 288))
        this.position = { top: `${icon.bottom + 6}px`, left: `${left}px` }
      }
      const method = isOpen ? "addEventListener" : "removeEventListener"
      document[method]("click", this.closeOutside, true)
      document[method]("keydown", this.closeOnEscape)
      window[method]("scroll", this.close, true)
      window[method]("resize", this.close)
    },
  },

  beforeDestroy() {
    document.removeEventListener("click", this.closeOutside, true)
    document.removeEventListener("keydown", this.closeOnEscape)
    window.removeEventListener("scroll", this.close, true)
    window.removeEventListener("resize", this.close)
  },

  methods: {
    close() {
      this.open = false
    },

    closeOutside(event) {
      if (!this.$refs.root.contains(event.target)) this.open = false
    },

    closeOnEscape(event) {
      if (event.key === "Escape") this.open = false
    },
  },
}
</script>

<style lang="scss" scoped>
.field-help {
  position: relative;
  display: inline-block;
  vertical-align: middle;
}

.field-help-button {
  display: inline-flex;
  padding: 2px;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: #b9b9c3;
  cursor: pointer;

  &:hover,
  &[aria-expanded="true"] {
    color: rgba(var(--vs-primary), 1);
  }
}

.field-help-popover {
  position: fixed;
  z-index: 52000;
  width: 280px;
  max-width: 80vw;
  padding: 10px 12px;
  border-radius: 6px;
  background: #2c2c2c;
  color: #fff;
  font-size: 0.8125rem;
  font-weight: 400;
  line-height: 1.4;
  text-align: left;
  white-space: normal;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.18);
}
</style>
